const STORAGE_KEY = "recentSegments";
const MAX_RECENT = 10;

export interface RecentSegment {
  id: string;
  name: string;
  accessedAt: number;
}

/**
 * Get the list of recently accessed segments from localStorage
 */
export function getRecentSegments(): RecentSegment[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];

    const segments: RecentSegment[] = JSON.parse(stored);
    // Sort by most recently accessed
    return segments.sort((a, b) => b.accessedAt - a.accessedAt);
  } catch {
    return [];
  }
}

/**
 * Add a segment to the recent segments list
 * If it already exists, update its accessedAt timestamp
 */
export function addRecentSegment(segment: { id: string; name: string }): void {
  try {
    const segments = getRecentSegments();

    // Remove existing entry if present
    const filtered = segments.filter((s) => s.id !== segment.id);

    // Add new entry at the beginning
    const updated: RecentSegment[] = [
      {
        id: segment.id,
        name: segment.name,
        accessedAt: Date.now(),
      },
      ...filtered,
    ].slice(0, MAX_RECENT);

    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch {
    // Silently fail if localStorage is not available
  }
}

/**
 * Update the name of a segment in the recent list
 * Useful when segment is renamed
 */
export function updateRecentSegmentName(id: string, name: string): void {
  try {
    const segments = getRecentSegments();
    const updated = segments.map((s) => (s.id === id ? { ...s, name } : s));
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch {
    // Silently fail if localStorage is not available
  }
}

/**
 * Remove a segment from the recent list
 * Useful when segment is deleted
 */
export function removeRecentSegment(id: string): void {
  try {
    const segments = getRecentSegments();
    const filtered = segments.filter((s) => s.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
  } catch {
    // Silently fail if localStorage is not available
  }
}

/**
 * Clear all recent segments
 */
export function clearRecentSegments(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Silently fail if localStorage is not available
  }
}
